import React from 'react';
import { Helmet } from 'react-helmet';

/**
 * LocalBusinessSchema Component
 * Provides structured data for local business SEO
 * Critical for "near me" searches and Google My Business integration
 * 
 * Update the address, phone, and other details with your actual business information
 */
const LocalBusinessSchema = ({
  name = "Miracle Lighting",
  description = "Professional wedding lighting services in Western Province, Colombo, Gampaha, Kalutara. Best event lighting company providing LED decoration, outdoor & indoor lighting for weddings, events, birthdays, corporate functions across Sri Lanka. වෙස්ටර්න් ප්‍රොවින්ස් විවාහ ආලෝක සේවා | කොළඹ උත්සව ආලෝක සේවා. Professional lighting services Sri Lanka.",
  telephone = "+94-714905424",
  email = "miraclelighting9@gmail.com",
  address = {
    streetAddress: "Kumudumawatha",
    addressLocality: "Colombo",
    addressRegion: "Western Province",
    postalCode: "10300",
    addressCountry: "LK"
  },
  geo = {
    latitude: "6.9271",
    longitude: "79.8612"
  },
  openingHours = [
    "Mo-Su 00:00-23:59"
  ],
  priceRange = "LKR",
  servicesOffered = [
    "Wedding Lighting Services Western Province",
    "Event Lighting Services Colombo",
    "Wedding Lighting Sri Lanka",
    "Light Decoration Services Sri Lanka",
    "Event Lighting Western Province",
    "Outdoor Wedding Lighting Sri Lanka",
    "Wedding Reception Lighting Colombo",
    "Professional Lighting Services Sri Lanka",
    "Party Lighting Services Western Province",
    "Corporate Event Lighting Sri Lanka",
    "LED Lighting Decoration Sri Lanka",
    "Stage and Party Lighting Colombo",
    "Custom Lighting Designs for Weddings",
    "Outdoor Event Lighting Sri Lanka",
    "LED Lights Installation",
    "String Lights Decoration",
    "Ceiling Light Installation",
    "Wall Light Setup",
    "Lamp Shades Supply",
    "Wedding Light Decoration",
    "Birthday Party Lighting",
    "Christmas Light Installation",
    "Kathina Festival Lighting",
    "Vesak Decorative Lighting",
    "Fairy Lights Installation",
    "Backdrop Lighting Services",
    "Chandeliers Installation",
    "Temple Decorative Lighting",
    "Pathway Lights Setup",
    "Indoor Lighting Services",
    "Outdoor Lighting Services"
  ]
}) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": "https://miraclelighting.lk/#organization",
    "name": name,
    "description": description,
    "url": "https://miraclelighting.lk",
    "telephone": telephone,
    "email": email,
    "priceRange": priceRange,
    "image": "https://miraclelighting.lk/cover.jpg",
    "logo": {
      "@type": "ImageObject",
      "url": "https://miraclelighting.lk/logo.png",
      "width": "250",
      "height": "60"
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": address.streetAddress,
      "addressLocality": address.addressLocality,
      "addressRegion": address.addressRegion,
      "postalCode": address.postalCode,
      "addressCountry": address.addressCountry
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": geo.latitude,
      "longitude": geo.longitude
    },
    "openingHoursSpecification": openingHours.map(hours => {
      const [days, time] = hours.split(' ');
      const [openTime, closeTime] = time.split('-');
      
      return {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": days.replace('Mo', 'Monday').replace('Tu', 'Tuesday')
          .replace('We', 'Wednesday').replace('Th', 'Thursday')
          .replace('Fr', 'Friday').replace('Sa', 'Saturday')
          .replace('Su', 'Sunday'),
        "opens": openTime,
        "closes": closeTime
      };
    }),
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Lighting Services",
      "itemListElement": servicesOffered.map((service, index) => ({
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": service
        }
      }))
    },
    "areaServed": [
      {
        "@type": "State",
        "name": "Western Province"
      },
      {
        "@type": "City",
        "name": "Colombo"
      },
      {
        "@type": "City",
        "name": "Gampaha"
      },
      {
        "@type": "City",
        "name": "Kalutara"
      },
      {
        "@type": "City",
        "name": "Kandy"
      },
      {
        "@type": "City",
        "name": "Negombo"
      },
      {
        "@type": "City",
        "name": "Galle"
      },
      {
        "@type": "City",
        "name": "Matara"
      },
      {
        "@type": "City",
        "name": "Kurunegala"
      },
      {
        "@type": "City",
        "name": "Anuradhapura"
      },
      {
        "@type": "Country",
        "name": "Sri Lanka"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/profile.php?id=100093008904723", // Update with actual social media
      "https://www.instagram.com/_miracle_lighting?utm_source=qr&igsh=amF0NGc3eGl5aWw1",
      "https://www.tiktok.com/@miracle_lighting?_t=ZS-8w6OT8FHphg&_r=1",
      "https://www.youtube.com/@Miracle_Lighting"  // Update with actual social media
    ]
  };

  // Breadcrumb Schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://miraclelighting.lk"
      }
    ]
  };

  // Website Schema
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Miracle Lighting",
    "url": "https://miraclelighting.lk",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://miraclelighting.lk/search?q={search_term_string}",
      "query-input": "required name=search_term_string"
    }
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
};

export default LocalBusinessSchema;
